import { useRef, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const computeOutliers = (data) => {
  const sorted = [...data].sort((a, b) => a - b);
  const q1 = percentile(sorted, 25);
  const q3 = percentile(sorted, 75);
  const iqr = q3 - q1;
  const lowerFence = q1 - 1.5 * iqr;
  const upperFence = q3 + 1.5 * iqr;

  const outliers = sorted.filter(val => val < lowerFence || val > upperFence);
  const nonOutliers = sorted.filter(val => val >= lowerFence && val <= upperFence);

  return {
    boxValues: [
      Math.min(...nonOutliers),
      q1,
      percentile(sorted, 50),
      q3,
      Math.max(...nonOutliers)
    ],
    outliers
  };
}


const percentile = (arr, p) => {
  const pos = (p / 100) * (arr.length - 1);
  const base = Math.floor(pos);
  const rest = pos - base;
  return arr[base + 1] !== undefined
    ? arr[base] + rest * (arr[base + 1] - arr[base])
    : arr[base];
}

// Main Component
const BoxPlot = ({ data, seriesName='', units, chartKey }) => {

  const chartComponentRef = useRef();
  const containerRef = useRef();
  const cleanedData = data.filter(value => typeof value === 'number' && !isNaN(value) && value !== null);
  const { boxValues, outliers } = useMemo(() => computeOutliers(cleanedData), [cleanedData]);

    const options = {
    chart: {
      type: 'boxplot',
      height: 250
    },
    title: {
      text: '',
    },
    credits: {
        enabled: false,
    },
    exporting: {
        enabled: false,
    },
    legend:{
        enabled:false,
    },
    xAxis: {
      categories: 'category',
      labels: { enabled: false },
      title: { text: '' },
      tickLength: 0, 
    },
    yAxis: {
      title: { text: '' },
    },
    plotOptions: {
        boxplot: {
            pointWidth: 50,
            fillColor: '#2ee0ca',
            lineWidth: 1,
            medianColor: 'gray',
            stemColor: 'gray',
            whiskerColor: 'gray'
        }
    },
    tooltip: {
        useHTML: true,
        positioner: () => {
            return { x: 400, y: 50 }; 
  },
  formatter() {
     if (this.point.low !== undefined) {
        return `
            <b>${seriesName}</b><br/><br/>
            <b>Min:</b> ${this.point.low}<br/>
            <b>Q1:</b> ${this.point.q1}<br/>
            <b>Median:</b> ${this.point.median}<br/>
            <b>Q3:</b> ${this.point.q3}<br/>
            <b>Max:</b> ${this.point.high}
        `;
    } else {
        return `<b>Outlier:</b> ${this.y}`;
    }
  }
},
    series: [
      {
        name: '',
        data: [boxValues],
        tooltip: {
          headerFormat: '<em>Sample</em><br/>',
        }
      },
      {
        name: 'Outliers',
        type: 'scatter',
        data: outliers.map(val => [0, val]), // x=0 for first/only category
        marker: {
          fillColor: '#3af207',
          lineWidth: 1,
          lineColor: Highcharts.getOptions().colors[2]
        },
        tooltip: {
          pointFormat: 'Outlier: {point.y}'
        }
      }
    ]
  };


  return (
    <div ref={containerRef} style={{ width: '100%', height: '250px' }}>
        <HighchartsReact
            highcharts={Highcharts}
            options={options}
            ref={chartComponentRef}
            key={chartKey}
        />
    </div>
  );
}

export default BoxPlot;